/**
 * Local persistence for Live Maneuvers page "selection sources" —
 * which sources get auto-selected (latest-per-source) on load and poll.
 * Separate from liveSourcesStore (display) and fleetPerformanceSources.
 */

import { debug } from './console';

const STORAGE_KEY = 'liveManeuverSelectionSources';

function storeKey(className: string, projectId: number | string): string {
  return `${className}_${projectId}`;
}

function readAll(): Record<string, number[]> {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, number[]>) : {};
  } catch {
    return {};
  }
}

function writeAll(data: Record<string, number[]>): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    debug('[LiveManeuverSelectionSources] Could not persist to localStorage', err);
  }
}

/** Load stored selection source IDs for a project, or null if none saved. */
export function getStoredLiveManeuverSelectionSources(
  className: string,
  projectId: number | string
): number[] | null {
  const cls = (className || '').trim();
  if (!cls) return null;
  const key = storeKey(cls, projectId);
  const all = readAll();
  const ids = all[key];
  if (!Array.isArray(ids) || ids.length === 0) return null;
  return ids
    .map((id) => Number(id))
    .filter((id): id is number => Number.isFinite(id) && id > 0);
}

/** Persist selection source IDs for a project (localStorage only). */
export function setStoredLiveManeuverSelectionSources(
  className: string,
  projectId: number | string,
  ids: number[]
): void {
  const cls = (className || '').trim();
  if (!cls) return;
  const key = storeKey(cls, projectId);
  const all = readAll();
  all[key] = [...new Set(ids.filter((id) => Number.isFinite(id) && id > 0))];
  writeAll(all);
  debug('[LiveManeuverSelectionSources] Saved selection sources', {
    key,
    count: all[key].length,
  });
}

const AUTO_UPDATE_STORAGE_KEY = 'liveManeuverAutoUpdate';

function readAllAutoUpdate(): Record<string, boolean> {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(AUTO_UPDATE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeAllAutoUpdate(data: Record<string, boolean>): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(AUTO_UPDATE_STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    debug('[LiveManeuverSelectionSources] Could not persist auto-update to localStorage', err);
  }
}

/** Load stored auto-update flag for a project, or null if none saved. */
export function getStoredLiveManeuverAutoUpdate(
  className: string,
  projectId: number | string
): boolean | null {
  const cls = (className || '').trim();
  if (!cls) return null;
  const key = storeKey(cls, projectId);
  const all = readAllAutoUpdate();
  const value = all[key];
  return typeof value === 'boolean' ? value : null;
}

/** Persist auto-update flag for a project (localStorage only). */
export function setStoredLiveManeuverAutoUpdate(
  className: string,
  projectId: number | string,
  enabled: boolean
): void {
  const cls = (className || '').trim();
  if (!cls) return;
  const key = storeKey(cls, projectId);
  const all = readAllAutoUpdate();
  all[key] = enabled;
  writeAllAutoUpdate(all);
  debug('[LiveManeuverSelectionSources] Saved auto-update flag', { key, enabled });
}
